import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const DateCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-12-31');
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [calculationMode, setCalculationMode] = useState<'difference' | 'countdown' | 'weekday'>('difference');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    if (!startDate || !endDate) return;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();

    // Diferença entre datas
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Calcular dias corridos (incluindo finais de semana)
    let elapsedDays = 0;
    let currentDate = new Date(start);

    while (currentDate <= end) {
      if (includeWeekends ||
        (currentDate.getDay() !== 0 && currentDate.getDay() !== 6)) {
        elapsedDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Calcular dias úteis (seg-sex)
    let workingDays = 0;
    currentDate = new Date(start);

    while (currentDate <= end) {
      if (currentDate.getDay() >= 1 && currentDate.getDay() <= 5) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Dias que faltam
    const daysUntilToday = Math.max(0, Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

    // Dia da semana
    const weekdays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const startWeekday = weekdays[start.getDay()];
    const endWeekday = weekdays[end.getDay()];

    // Detalhamento em anos, meses, dias
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;

    const res = {
      totalDays: diffDays,
      elapsedDays,
      workingDays,
      daysUntilToday,
      startWeekday,
      endWeekday,
      years,
      months,
      days,
      isPast: end < today,
      isFuture: end > today
    };

    setResult(res);

    const data = [{ label: 'Modo', value: calculationMode }];
    if (calculationMode === 'difference') {
      data.push({ label: 'Dias Corridos', value: `${res.totalDays} dias` });
      data.push({ label: 'Dias Úteis', value: `${res.workingDays} dias` });
      data.push({ label: 'Detalhamento', value: `${res.years}a, ${res.months}m, ${res.days}d` });
    } else if (calculationMode === 'countdown') {
      data.push({ label: 'Dias que Faltam', value: `${res.daysUntilToday} dias` });
    } else {
      data.push({ label: 'Início', value: res.startWeekday });
      data.push({ label: 'Fim', value: res.endWeekday });
    }
    onResultChange(data);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        {/* Seletor de modo */}
        <div className="mb-6">
          <FormField
            label="Modo de Cálculo"
            value={calculationMode}
            onChange={setCalculationMode}
            type="select"
            required
            options={[
              { value: 'difference', label: 'Diferença entre Datas' },
              { value: 'countdown', label: 'Dias que Faltam' },
              { value: 'weekday', label: 'Dia da Semana' }
            ]}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest flex items-center gap-1 mb-2">
              Data Inicial
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 bg-theme-result border border-theme-border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              required
            />
          </div>
          <div>
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest flex items-center gap-1 mb-2">
              Data Final
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 bg-theme-result border border-theme-border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              required
            />
          </div>
          {calculationMode === 'difference' && (
            <FormField
              label="Incluir Finais de Semana"
              value={includeWeekends ? 'true' : 'false'}
              onChange={(value) => setIncludeWeekends(value === 'true')}
              type="select"
              options={[
                { value: 'true', label: 'Sim' },
                { value: 'false', label: 'Não' }
              ]}
            />
          )}
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Datas
          </button>
        </div>

        {result && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {calculationMode === 'difference' && (
              <>
                <ResultCard
                  title="Dias Corridos"
                  value={`${result.totalDays} dias`}
                  description="Total de dias incluindo finais de semana"
                  color="blue"
                />
                <ResultCard
                  title="Dias Úteis"
                  value={`${result.workingDays} dias`}
                  description="Apenas dias de semana (seg-sex)"
                  color="green"
                />
                <ResultCard
                  title="Detalhamento"
                  value={`${result.years}a, ${result.months}m, ${result.days}d`}
                  description="Anos, meses e dias"
                  color="purple"
                />
              </>
            )}

            {calculationMode === 'countdown' && (
              <>
                <ResultCard
                  title="Dias que Faltam"
                  value={`${result.daysUntilToday} dias`}
                  description={result.isPast ? 'Data já passou' : 'Para a data selecionada'}
                  color="orange"
                />
                <ResultCard
                  title="Data Inicial"
                  value={result.startWeekday}
                  description={startDate}
                  color="slate"
                />
                <ResultCard
                  title="Data Final"
                  value={result.endWeekday}
                  description={endDate}
                  color="slate"
                />
              </>
            )}

            {calculationMode === 'weekday' && (
              <>
                <ResultCard
                  title="Dia da Semana - Início"
                  value={result.startWeekday}
                  description={startDate}
                  color="indigo"
                />
                <ResultCard
                  title="Dia da Semana - Fim"
                  value={result.endWeekday}
                  description={endDate}
                  color="indigo"
                />
                <ResultCard
                  title="Dias de Diferença"
                  value={`${result.totalDays} dias`}
                  description="Entre as duas datas"
                  color="blue"
                />
              </>
            )}
          </div>
        )}
      </div>

      <EducationalBlock
        title="Como Funciona a Calculadora de Datas"
        tips={[
          'Dias corridos: Contagem total incluindo finais de semana',
          'Dias úteis: Apenas dias de semana (segunda a sexta)',
          'Diferença: Cálculo exato entre duas datas',
          'Contagem regressiva: Dias que faltam para uma data futura',
          'Anos bissextos são considerados automaticamente'
        ]}
      />
    </div>
  );
};

export default DateCalculator;
